import React, { useEffect, useState, useCallback } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faRightFromBracket,
  faShieldHalved,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons'

import { ConfirmModal } from '../components/Modal'
import {
  isPushSupported,
  isIOS,
  isStandalone,
  currentPermission,
  enablePush,
  disablePush,
  getPreferences,
} from '../lib/pushNotifications'

const AVATAR_COLORS = [
  '#0f766e',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#0369a1',
  '#15803d',
  '#b45309',
  '#7e22ce',
  '#1d4ed8',
  '#be123c',
]

function colorFromString(s) {
  const str = s || ''
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function initialsOf(name, fallback) {
  const src = (name || fallback || '').trim()
  if (!src) return '?'
  const parts = src.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function roleLabel(t) {
  if (t === 'admin') return 'Admin'
  if (t === 'producer') return 'Producer'
  if (t === 'single') return 'Singolo'
  return t || ''
}

function roleIcon(t) {
  if (t === 'admin') return faShieldHalved
  if (t === 'producer') return faUsers
  return faUser
}

function Settings({ user, logout }) {
  const isAdmin = user && user.type === 'admin'
  const supported = isPushSupported()
  const ios = isIOS()
  const standalone = isStandalone()

  const [enabled, setEnabled] = useState(false)
  const [permission, setPermission] = useState(currentPermission())
  const [pushConfigured, setPushConfigured] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [logoutOpen, setLogoutOpen] = useState(false)
  const history = useHistory()

  // Carica preferenze al mount per non-admin con push supportato.
  useEffect(() => {
    let cancelled = false
    if (isAdmin || !supported) return undefined
    ;(async () => {
      const prefs = await getPreferences()
      if (cancelled) return
      if (prefs) {
        setEnabled(!!prefs.enabled)
        if (typeof prefs.pushConfigured === 'boolean')
          setPushConfigured(prefs.pushConfigured)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAdmin, supported])

  const onActivate = useCallback(async () => {
    setError('')
    setLoading(true)
    const r = await enablePush()
    setLoading(false)
    setPermission(currentPermission())
    if (r.ok) {
      setEnabled(true)
      return
    }
    if (r.error === 'permission_denied') {
      setError(
        'Hai bloccato le notifiche per questo sito. Riabilitale dalle impostazioni del browser e riprova.',
      )
      return
    }
    if (r.error === 'unsupported') {
      setError('Il tuo browser non supporta le notifiche push.')
      return
    }
    if (r.error === 'vapid_not_configured') {
      setError('Le notifiche non sono ancora configurate sul server.')
      return
    }
    setError("Attivazione non riuscita. Riprova fra un istante.")
  }, [])

  const onDeactivate = useCallback(async () => {
    setError('')
    setLoading(true)
    const r = await disablePush()
    setLoading(false)
    if (r.ok) {
      setEnabled(false)
      return
    }
    setError('Disattivazione non riuscita. Riprova fra un istante.')
  }, [])

  const onLogoutClick = () => setLogoutOpen(true)
  const onLogoutConfirm = async () => {
    setLogoutOpen(false)
    if (typeof logout === 'function') {
      await logout()
    } else {
      // Fallback: la prop dovrebbe esserci, ma non ci blocchiamo se manca.
      try {
        localStorage.removeItem('cut-catering-token')
      } catch (e) {
        /* ignore */
      }
    }
    history.replace('/login')
  }

  const avatarColor = colorFromString(user && (user.name || user.email))
  const initials = initialsOf(user && user.name, user && user.email)

  return (
    <div className="settings-page">
      <div className="settings-header">
        <Link
          to={isAdmin ? '/dashboard' : '/order'}
          className="settings-back"
          aria-label="Torna indietro"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </Link>
        <h1 className="settings-page-title">Impostazioni</h1>
      </div>

      {/* Profilo */}
      <div className="settings-profile">
        <div
          className="settings-profile-avatar"
          style={{ backgroundColor: avatarColor }}
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="settings-profile-text">
          <h2 className="settings-profile-name">
            {(user && user.name) || 'Senza nome'}
          </h2>
          <div className="settings-profile-email">
            {(user && user.email) || ''}
          </div>
          <div className="settings-profile-meta">
            <span className="settings-profile-chip">
              <FontAwesomeIcon icon={roleIcon(user && user.type)} />
              {roleLabel(user && user.type)}
            </span>
            {user && user.customer ? (
              <span className="settings-profile-chip">{user.customer}</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Notifiche */}
      {!isAdmin && (
        <div className="settings-section">
          <h3 className="settings-section-title">Notifiche</h3>
          <div className="settings-section-body">
            {!supported ? (
              <p className="settings-row-sub">
                Il tuo browser non supporta le notifiche push.
              </p>
            ) : !pushConfigured ? (
              <p className="settings-row-sub">
                Il servizio di notifiche non è ancora configurato sul server.
              </p>
            ) : (
              <>
                <div className="settings-row">
                  <div className="settings-row-text">
                    <div className="settings-row-label">
                      Promemoria 15 min prima della chiusura
                    </div>
                    <div className="settings-row-sub">
                      Una sola notifica al giorno, solo se non hai ancora
                      ordinato per il pasto in arrivo.
                    </div>
                  </div>
                  <label className="broadcast-switch">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() =>
                        enabled ? onDeactivate() : onActivate()
                      }
                      disabled={loading}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                {permission === 'denied' && (
                  <p className="settings-row-sub" style={{ color: '#b30000' }}>
                    Hai bloccato le notifiche per questo sito. Riabilitale dalle
                    impostazioni del browser e ricarica la pagina.
                  </p>
                )}

                {ios && !standalone && (
                  <div
                    className="notifications-cta-ios"
                    style={{
                      marginTop: 12,
                      padding: 14,
                      borderRadius: 10,
                      border: '1px solid #f3d77a',
                      color: '#5a3f00',
                      background: '#fff7e0',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    <b>Per ricevere le notifiche su iPhone</b> serve aprire
                    l'app dalla Home. Se hai un'icona vecchia, rimuovila, apri
                    questa pagina in Safari, tocca <b>Condividi → Aggiungi alla
                    schermata Home</b> e riapri l'app dall'icona nuova.
                  </div>
                )}

                {error && (
                  <p className="settings-row-sub" style={{ color: '#b30000' }}>
                    {error}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="settings-section">
          <h3 className="settings-section-title">Notifiche</h3>
          <div className="settings-section-body">
            <p className="settings-row-sub">
              I promemoria automatici sono attivabili solo dagli utenti
              producer e single. Come admin puoi gestire le notifiche
              broadcast a tutti dalla pagina <b>Comunicazioni</b>.
            </p>
          </div>
        </div>
      )}

      {/* Logout in fondo */}
      <div className="settings-divider" />
      <div className="settings-logout-row">
        <button
          type="button"
          className="settings-logout-btn"
          onClick={onLogoutClick}
        >
          <FontAwesomeIcon icon={faRightFromBracket} />
          Esci dall'app
        </button>
        <p className="settings-note">
          Dovrai accedere di nuovo con email e password — oppure con il QR
          code — per tornare.
        </p>
      </div>

      <ConfirmModal
        open={logoutOpen}
        title="Vuoi uscire dall'app?"
        message="Dovrai accedere di nuovo con email e password (oppure con il QR code) per tornare. La tua password non verrà cambiata."
        confirmLabel="Esci ora"
        cancelLabel="Annulla"
        danger
        onConfirm={onLogoutConfirm}
        onCancel={() => setLogoutOpen(false)}
      />
    </div>
  )
}

export default Settings
